/**
 * Leave Calendar Component
 * Shows who is on leave in a calendar view
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Calendar as CalendarIcon, 
  ChevronLeft, 
  ChevronRight,
  User,
  AlertCircle
} from 'lucide-react';
import { formatCalendarDate, parseCalendarDate } from '../../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL || '';

const LEAVE_TYPE_COLORS = {
  casual: 'bg-blue-100 text-blue-700 border-blue-200',
  sick: 'bg-red-100 text-red-700 border-red-200',
  planned: 'bg-green-100 text-green-700 border-green-200',
  emergency: 'bg-orange-100 text-orange-700 border-orange-200'
};

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const LeaveCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [leaveEntries, setLeaveEntries] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1;

  const fetchCalendarData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/leave/calendar`, {
        params: { month, year }
      });
      setLeaveEntries(response.data.entries || []);
      setHolidays(response.data.holidays || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching calendar:', err);
      setError('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => {
    fetchCalendarData();
  }, [fetchCalendarData]);

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
    setSelectedDate(null);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(null);
  };

  // Generate calendar grid
  const generateCalendar = () => {
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const startDay = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    
    const calendar = [];
    let day = 1;
    
    for (let week = 0; week < 6; week++) {
      const weekDays = [];
      for (let dayOfWeek = 0; dayOfWeek < 7; dayOfWeek++) {
        if (week === 0 && dayOfWeek < startDay) {
          weekDays.push(null);
        } else if (day > daysInMonth) {
          weekDays.push(null);
        } else {
          weekDays.push(day);
          day++;
        }
      }
      if (weekDays.some(d => d !== null)) {
        calendar.push(weekDays);
      }
    }
    
    return calendar;
  };

  // Get leaves for a specific date
  const getLeavesForDate = (day) => {
    if (!day) return [];
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    // parseCalendarDate keeps everything in local-midnight wall-clock
    // space, so the range comparison stays correct regardless of the
    // browser timezone (avoids the UTC-midnight footgun).
    const current = parseCalendarDate(dateStr);
    if (!current) return [];
    return leaveEntries.filter(entry => {
      // LEAVE-SANDWICH-002: bridge dates charged to this request also show
      if (Array.isArray(entry.sandwich_bridge_dates) && entry.sandwich_bridge_dates.includes(dateStr)) return true;
      const start = parseCalendarDate(entry.start_date);
      const end = parseCalendarDate(entry.end_date);
      if (!start || !end) return false;
      return current >= start && current <= end;
    });
  };

  // Check if date is today
  const isToday = (day) => {
    const today = new Date();
    return day === today.getDate() && 
           month === today.getMonth() + 1 && 
           year === today.getFullYear();
  };

  const calendar = generateCalendar();

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-red-700">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CalendarIcon className="w-6 h-6 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">Leave Calendar</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigateMonth(-1)}
              className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={goToToday}
              className="px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              Today
            </button>
            <button
              onClick={() => navigateMonth(1)}
              className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
        <p className="text-xl font-bold text-gray-800 mt-2">
          {MONTHS[month - 1]} {year}
        </p>
      </div>

      {/* Calendar Grid */}
      <div className={`${loading ? 'opacity-50' : ''}`}>
        {/* Days header */}
        <div className="grid grid-cols-7 border-b border-gray-100">
          {DAYS_OF_WEEK.map(day => (
            <div key={day} className="p-2 text-center text-xs font-medium text-gray-500 uppercase">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar days */}
        <div className="divide-y divide-gray-100">
          {calendar.map((week, weekIndex) => (
            <div key={weekIndex} className="grid grid-cols-7 min-h-[100px]">
              {week.map((day, dayIndex) => {
                const dayLeaves = getLeavesForDate(day);
                const hasLeaves = dayLeaves.length > 0;
                const dateKey = day ? `${year}-${month}-${day}` : `empty-${weekIndex}-${dayIndex}`;
                // LEAVE-CAL-UI-001: weekends & company holidays are shown for
                // continuity but are NOT deducted from the leave balance.
                const dateStr = day ? `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}` : null;
                const isHoliday = !!dateStr && holidays.includes(dateStr);
                const nonChargeable = !!day && (dayIndex === 0 || dayIndex === 6 || isHoliday);
                
                return (
                  <div 
                    key={dateKey}
                    onClick={() => day && hasLeaves && setSelectedDate({ day, leaves: dayLeaves })}
                    className={`p-1 border-r border-gray-100 last:border-r-0 relative ${
                      !day ? 'bg-gray-50' : nonChargeable ? 'bg-slate-50' : 'bg-white'
                    } ${hasLeaves ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                  >
                    {day && (
                      <>
                        <span className={`text-sm font-medium inline-flex items-center justify-center w-7 h-7 rounded-full ${
                          isToday(day) 
                            ? 'bg-blue-600 text-white' 
                            : nonChargeable ? 'text-gray-400' : 'text-gray-700'
                        }`}>
                          {day}
                        </span>
                        {isHoliday && (
                          <span className="ml-1 text-[10px] uppercase tracking-wide text-amber-600 font-medium" data-testid="calendar-holiday-tag">Holiday</span>
                        )}
                        
                        {/* Leave indicators */}
                        <div className="mt-1 space-y-1">
                          {dayLeaves.slice(0, 2).map((leave, idx) => {
                            // LEAVE-SANDWICH-001: chargeable = inside the
                            // request's first→last working-day window (clubbed
                            // weekends/holidays ARE deducted); only leading/
                            // trailing non-working days are not charged.
                            const chargeable = leave.chargeable_start
                              ? (dateStr >= leave.chargeable_start && dateStr <= leave.chargeable_end)
                              : true;
                            const pending = leave.status === 'pending';
                            const bridge = Array.isArray(leave.sandwich_bridge_dates) && leave.sandwich_bridge_dates.includes(dateStr);
                            if (bridge) {
                              const prov = leave.sandwich_bridge_status === 'provisional';
                              return (
                                <div key={idx} data-testid="leave-chip-sandwich" data-status={prov ? 'provisional' : 'confirmed'}
                                  title={`Sandwich leave — chargeable holiday/weekly-off between two continuous leave requests${prov ? ' (provisional until both are approved)' : ''}`}
                                  className={`text-xs px-1.5 py-0.5 rounded truncate text-amber-900 border border-amber-500 ${prov ? 'border-dashed' : ''}`}
                                  style={{ background: 'repeating-linear-gradient(135deg, #fef3c7 0 6px, #fde68a 6px 12px)' }}>
                                  {leave.user_full_name?.split(' ')[0] || 'User'}<span className="ml-1 text-[9px] font-bold uppercase tracking-wide">· Sandwich</span>
                                </div>
                              );
                            }
                            return (
                              <div 
                                key={idx}
                                data-testid={chargeable ? 'leave-chip' : 'leave-chip-nonchargeable'}
                                data-status={pending ? 'pending' : 'approved'}
                                title={pending
                                  ? 'Pending approval — informational only'
                                  : !chargeable
                                  ? 'Leading/trailing weekend or holiday — not deducted'
                                  : nonChargeable
                                    ? 'Clubbed weekend/holiday within continuous leave — deducted (sandwich policy)'
                                    : undefined}
                                className={`text-xs px-1.5 py-0.5 rounded truncate ${
                                  LEAVE_TYPE_COLORS[leave.leave_type] || LEAVE_TYPE_COLORS.casual
                                }${pending ? ' border border-dashed border-current opacity-80' : chargeable ? '' : ' opacity-45 border border-dashed'}`}
                              >
                                {leave.user_full_name?.split(' ')[0] || 'User'}{pending && <span className="ml-1 text-[9px] font-bold uppercase tracking-wide" data-testid="leave-chip-pending-tag">· Pending</span>}
                              </div>
                            );
                          })}
                          {dayLeaves.length > 2 && (
                            <div className="text-xs text-gray-500 px-1.5">
                              +{dayLeaves.length - 2} more
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="p-4 border-t border-gray-100 bg-gray-50">
        <div className="flex flex-wrap gap-4">
          {Object.entries(LEAVE_TYPE_COLORS).map(([type, colors]) => (
            <div key={type} className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded ${colors.split(' ')[0]}`}></div>
              <span className="text-xs text-gray-600 capitalize">{type}</span>
            </div>
          ))}
          <div className="flex items-center gap-2" data-testid="legend-approved">
            <div className="w-3 h-3 rounded bg-emerald-100 border border-emerald-400"></div>
            <span className="text-xs text-gray-600">Approved (solid)</span>
          </div>
          <div className="flex items-center gap-2" data-testid="legend-pending">
            <div className="w-3 h-3 rounded border border-dashed border-amber-500 bg-amber-50"></div>
            <span className="text-xs text-gray-600">Pending approval (dashed) — informational only</span>
          </div>
          <div className="flex items-center gap-2" data-testid="legend-nonchargeable">
            <div className="w-3 h-3 rounded border border-dashed border-gray-400 bg-gray-100 opacity-60"></div>
            <span className="text-xs text-gray-600">Leading/trailing weekend or holiday — not charged (clubbed weekends inside continuous leave ARE deducted)</span>
          </div>
        </div>
      </div>

      {/* Selected Date Modal */}
      {selectedDate && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setSelectedDate(null)}
        >
          <div 
            className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {MONTHS[month - 1]} {selectedDate.day}, {year}
            </h3>
            <div className="space-y-3">
              {selectedDate.leaves.map((leave, idx) => (
                <div 
                  key={idx}
                  data-testid="modal-leave-entry"
                  data-status={leave.status === 'pending' ? 'pending' : 'approved'}
                  className={`p-3 rounded-lg border ${LEAVE_TYPE_COLORS[leave.leave_type] || LEAVE_TYPE_COLORS.casual}${leave.status === 'pending' ? ' border-dashed opacity-90' : ''}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <User className="w-4 h-4" />
                    <span className="font-medium">{leave.user_full_name}</span>
                    <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${leave.status === 'pending' ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-300' : 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-300'}`} data-testid={`modal-status-${leave.status === 'pending' ? 'pending' : 'approved'}`}>{leave.status === 'pending' ? 'Pending' : 'Approved'}</span>
                  </div>
                  <p className="text-sm capitalize">
                    {leave.leave_type} Leave
                  </p>
                  <p className="text-xs mt-1 opacity-75">
                    {formatCalendarDate(leave.start_date)} - {formatCalendarDate(leave.end_date)}
                  </p>
                  {typeof leave.total_days === 'number' && (
                    <p className="text-xs mt-0.5 font-medium" data-testid="modal-chargeable-days">
                      {leave.total_days} chargeable day{leave.total_days === 1 ? '' : 's'} (sandwich policy — clubbed weekends &amp; holidays included)
                    </p>
                  )}
                </div>
              ))}
            </div>
            <button
              onClick={() => setSelectedDate(null)}
              className="w-full mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveCalendar;
